import { useCallback, useMemo } from 'react';

import type { UseActionDashboardQueryData } from './useActionDashboardQuery';
import type {
	AcceptanceTimeItem,
	ResponseRatioItem,
	TotalActionsPerOrganizationItem,
} from '@/app/types/Dashboard';
import type { Workbook, Worksheet } from 'exceljs';

type CellValue = string | number | null | undefined;
type SheetRow = Record<string, CellValue>;

const autoSizeWorksheetColumns = (ws: Worksheet) => {
	for (let i = 1; i <= ws.columnCount; i += 1) {
		const column = ws.getColumn(i);
		let max = 10;

		column.eachCell({ includeEmpty: true }, (cell) => {
			const value = cell.value == null ? '' : String(cell.value as string);
			max = Math.max(max, value.length + 2);
		});

		column.width = max;
	}
};

const addWorksheet = (wb: Workbook, sheetName: string, headers: string[], rows: SheetRow[]) => {
	if (!rows.length) {
		return;
	}

	const ws = wb.addWorksheet(sheetName);
	ws.addRow(headers);

	for (const row of rows) {
		ws.addRow(Object.values(row));
	}

	const headerRow = ws.getRow(1);
	headerRow.font = { bold: true };

	autoSizeWorksheetColumns(ws);
};

const createTotalActionsRows = (items: TotalActionsPerOrganizationItem[]) => {
	return items.map((item) => ({
		organizationName: item.organizationName,
		organizationTotal: item.organizationTotal,
	}));
};

const createAcceptedTimeRows = (items: AcceptanceTimeItem[]) => {
	return items.map((item) => ({
		organizationName: item.organizationName,
		averageTime: item.averageTime,
	}));
};

const createResponseRatioRows = (items: ResponseRatioItem[]) => {
	return items.map((item) => ({
		organizationName: item.organizationName,
		accepted: item.accepted,
		rejected: item.rejected,
		unanswered: item.unanswered,
		total: item.total,
	}));
};

const downloadBufferAsXlsx = (buffer: BlobPart, fileName: string) => {
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});

	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
};

export const useActionDashboardWorkSheets = (data: UseActionDashboardQueryData['data']) => {
	const {
		totalActionsSeries = [],
		acceptedTimeSeries = [],
		responseRationSeries = [],
	} = data ?? {};

	const generateFile = useCallback(
		async (fileName: string) => {
			const { Workbook } = await import('exceljs');
			const wb = new Workbook();

			addWorksheet(
				wb,
				'Total Actions',
				['Organization Name', 'Total Actions'],
				createTotalActionsRows(totalActionsSeries)
			);

			addWorksheet(
				wb,
				'Average Time',
				['Organization Name', 'Average Time'],
				createAcceptedTimeRows(acceptedTimeSeries)
			);

			addWorksheet(
				wb,
				'Response Ratio',
				['Organization Name', 'Accepted', 'Rejected', 'Unanswered', 'Total'],
				createResponseRatioRows(responseRationSeries)
			);

			const buffer = await wb.xlsx.writeBuffer();
			downloadBufferAsXlsx(buffer, fileName);
		},
		[acceptedTimeSeries, responseRationSeries, totalActionsSeries]
	);

	return useMemo(() => {
		const hasData =
			totalActionsSeries.length > 0 ||
			acceptedTimeSeries.length > 0 ||
			responseRationSeries.length > 0;

		return [generateFile, { hasData }] as const;
	}, [
		acceptedTimeSeries.length,
		generateFile,
		responseRationSeries.length,
		totalActionsSeries.length,
	]);
};
